import React, { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, MessageCircle, Mail, Send, Loader2, Check, Info } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { IMAGES, CONTACT, serviceOptions } from "../lib/data";
import { createBooking } from "../lib/api";

const inputClass = "border-white/10 bg-matte-deep text-white placeholder:text-white/30 focus-visible:ring-gold h-12 rounded-none";
const labelClass = "font-body text-xs uppercase tracking-[0.15em] text-white/60";

export default function Booking() {
  const { t, lang } = useLang();
  const [date, setDate] = useState(null);
  const [service, setService] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", time: "", pickup: "", destination: "", pickupCoords: "", destinationCoords: "", passengers: 1, luggage: 0, message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !service) {
      toast.error(lang === "it" ? "Seleziona data e tipo di servizio." : "Please select date and service type.");
      return;
    }
    if (!form.pickup.trim() || !form.destination.trim()) {
      toast.error(lang === "it" ? "Inserisci partenza e destinazione." : "Please enter pickup and destination.");
      return;
    }
    if (!consent) {
      toast.error(t("legal.consentReq"));
      return;
    }
    setSubmitting(true);
    try {
      await createBooking({
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: format(date, "yyyy-MM-dd"),
        time: form.time,
        pickup: form.pickup,
        destination: form.destination,
        pickup_coords: form.pickupCoords,
        destination_coords: form.destinationCoords,
        passengers: Number(form.passengers) || 1,
        luggage: Number(form.luggage) || 0,
        service_type: service,
        message: form.message,
        lang,
      });
      toast.success(t("booking.success"));
      setForm({ name: "", phone: "", email: "", time: "", pickup: "", destination: "", pickupCoords: "", destinationCoords: "", passengers: 1, luggage: 0, message: "" });
      setDate(null);
      setService("");
      setConsent(false);
    } catch (err) {
      toast.error(lang === "it" ? "Errore durante l'invio. Riprova." : "Error sending request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero tag={t("booking.tag")} title={t("booking.title")} subtitle={t("booking.subtitle")} image={IMAGES.fleet2} />

      <section className="mx-auto max-w-6xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-[1.6fr_1fr]">
          <Reveal>
            <form data-testid="booking-form" onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>{t("booking.date")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="booking-date-trigger"
                        className={`flex h-12 items-center justify-between border border-white/10 bg-matte-deep px-3 text-left font-body text-sm ${date ? "text-white" : "text-white/30"}`}>
                        {date ? format(date, "dd/MM/yyyy") : t("booking.pickDate")}
                        <CalendarIcon size={16} className="text-gold" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto border-white/10 bg-matte-surface p-0" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus className="text-white" />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>{t("booking.time")}</Label>
                  <Input data-testid="booking-time" type="time" value={form.time} onChange={set("time")} required className={inputClass} />
                </div>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>{t("booking.pickup")}</Label>
                  <AddressAutocomplete
                    testid="booking-pickup"
                    value={form.pickup}
                    onChange={(v) => setField("pickup", v)}
                    onSelect={(s) => setForm((f) => ({ ...f, pickup: s.label, pickupCoords: `${s.lat},${s.lon}` }))}
                    placeholder={t("booking.pickupPh")}
                    lang={lang}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>{t("booking.destination")}</Label>
                  <AddressAutocomplete
                    testid="booking-destination"
                    value={form.destination}
                    onChange={(v) => setField("destination", v)}
                    onSelect={(s) => setForm((f) => ({ ...f, destination: s.label, destinationCoords: `${s.lat},${s.lon}` }))}
                    placeholder={t("booking.destinationPh")}
                    lang={lang}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid gap-6 sm:grid-cols-3">
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>{t("booking.passengers")}</Label>
                  <Input data-testid="booking-passengers" type="number" min="1" value={form.passengers} onChange={set("passengers")} className={inputClass} />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>{t("booking.luggage")}</Label>
                  <Input data-testid="booking-luggage" type="number" min="0" value={form.luggage} onChange={set("luggage")} className={inputClass} />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>{t("booking.serviceType")}</Label>
                  <Select value={service} onValueChange={setService}>
                    <SelectTrigger data-testid="booking-service" className={`${inputClass} data-[placeholder]:text-white/30`}>
                      <SelectValue placeholder={t("booking.selectService")} />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-matte-surface text-white">
                      {serviceOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value} className="focus:bg-gold focus:text-black">{o[lang]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>{t("booking.name")}</Label>
                  <Input data-testid="booking-name" value={form.name} onChange={set("name")} required className={inputClass} />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>{t("booking.phone")}</Label>
                  <Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} required className={inputClass} />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label className={labelClass}>{t("booking.email")}</Label>
                <Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} required className={inputClass} />
                <p data-testid="booking-email-hint" className="flex items-start gap-2 font-body text-xs leading-relaxed text-gold/80">
                  <Info size={13} className="mt-0.5 shrink-0" /> {t("booking.emailHint")}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <Label className={labelClass}>{t("booking.message")}</Label>
                <Textarea data-testid="booking-message" value={form.message} onChange={set("message")} placeholder={t("booking.messagePh")} className={`${inputClass} h-auto min-h-[120px] py-3`} />
                <p data-testid="booking-message-hint" className="flex items-start gap-2 font-body text-xs leading-relaxed text-white/50">
                  <Info size={13} className="mt-0.5 shrink-0 text-gold" /> {t("booking.messageHint")}
                </p>
              </div>

              <label className="flex items-start gap-3 pt-2">
                <Checkbox data-testid="booking-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:text-black" />
                <span className="font-body text-xs leading-relaxed text-white/60">
                  {t("legal.consent")}{" "}
                  <Link to="/privacy" target="_blank" className="text-gold underline underline-offset-2">{t("legal.privacyLink")}</Link>.
                </span>
              </label>

              <button type="submit" data-testid="booking-submit" disabled={submitting}
                className="flex w-full items-center justify-center gap-2 bg-gold py-4 font-body text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-muted disabled:opacity-60">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {submitting ? t("booking.submitting") : t("booking.submit")}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="flex flex-col gap-6 self-start border border-white/10 bg-matte-surface p-8">
            <h3 className="font-heading text-2xl text-white">{t("booking.orContact")}</h3>
            <a data-testid="booking-whatsapp" href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 border border-white/10 bg-matte-deep px-5 py-4 transition-colors hover:border-[#25D366]">
              <MessageCircle size={22} className="text-[#25D366]" />
              <span className="font-body text-sm text-white">WhatsApp · {CONTACT.phoneIntl}</span>
            </a>
            <a data-testid="booking-email-btn" href={`mailto:${CONTACT.email}`}
              className="flex items-center gap-3 border border-white/10 bg-matte-deep px-5 py-4 transition-colors hover:border-gold">
              <Mail size={22} className="text-gold" />
              <span className="font-body text-sm text-white">{CONTACT.email}</span>
            </a>
            <div className="mt-2 space-y-3 border-t border-white/10 pt-6 font-body text-sm text-white/55">
              {[t("about.v1d"), t("about.v2d"), t("about.v3d")].map((li) => (
                <p key={li} className="flex items-start gap-2"><Check size={16} className="mt-0.5 shrink-0 text-gold" /> {li}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
